import { FormView } from '../../UI/view/form-view';
import { FormItemType } from '../../UI/view/component-map';
import { initView } from '../../UI/view/view';
import { getAllLocations } from '../../../services/requests';
import { getOrderDeliveryTypes, getCarriers, getActiveUsers, getContainerOptions, getReturningContainerOptions, getAvailableBookings, getContainerInfo } from '../../../services/requests/orders/orders-requests';
import { ordersCreateDS, ordersPermissions } from './orders-datasource';
import { OrderType } from './orders-types';
import { OrderCustomerPickerButton } from './orders-customer-picker-widget';
import { OrderContactField } from './orders-contact-field';
import { OrderCustomerClipboard, OrderParentLink, OrderCreditCardOverride } from './orders-info-widgets';
import { OrderCreditCardStatus } from './orders-credit-card-status';
import { OrderLocationStatusListView } from './orders-location-status-list';
import {
  GasRecentDeliveriesListView,
  LiquidRecentDeliveriesListView,
  RecentDewarDeliveriesListView,
  DewarContainersOnSiteListView,
  UpcomingOrdersForCustomerListView,
  GasContainersOnSiteListView,
  LiquidContainersOnSiteListView,
  CustomerPurityListView,
} from './orders-current-customer';

// Field-level visibility helpers (evaluated with the live form via `watch`):
// Liquid-only Paperwork / High-Pressure Container / Forecasted; Dewar-only
// Credit Card Payment + the Dewar customer labels.
const isLiquid = ({ watch }: any) => Number(watch?.('orderTypeId')) === OrderType.Liquid;
const isDewar = ({ watch }: any) => Number(watch?.('orderTypeId')) === OrderType.Dewar;
const isGas = ({ watch }: any) => Number(watch?.('orderTypeId')) === OrderType.Gas;
const hideUnlessLiquid = ({ watch }: any) => !isLiquid({ watch });
const hideUnlessDewar = ({ watch }: any) => !isDewar({ watch });
const hideUnlessGas = ({ watch }: any) => !isGas({ watch });
const hideIfDewar = ({ watch }: any) => isDewar({ watch });

// Tab-level visibility helpers. Tab `hide` receives (state, watch); these read
// the live form via `watch` (the tab control subscribes to `watchFields` below).
const tabHideUntilOrderType = (_state: any, watch?: any) => !Number(watch?.('orderTypeId'));
const tabHideUnlessGasOrLiquid = (_state: any, watch?: any) => {
  const t = Number(watch?.('orderTypeId'));
  return !(t === OrderType.Gas || t === OrderType.Liquid);
};
// International Dewar (legacy changeIntlDewarsDisplay): Dewar + a US territory /
// international ship-to state (ZZ / PR / GU).
const tabHideUnlessIntlDewar = (_state: any, watch?: any) => {
  if (Number(watch?.('orderTypeId')) !== OrderType.Dewar) return true;
  const st = String(watch?.('state') ?? '').trim();
  return !(st === 'ZZ' || st === 'PR' || st === 'GU');
};

// Date Ordered defaults to "today" like the legacy tbDateOrdered pre-fill.
const today = new Date();

// Hour-of-day (0-23) options for the Time Requested dropdown, mirroring the
// legacy ddlTimeRequested. Kept as a separate field from Date Requested so the
// time can be auto-set on order-type change without also auto-setting the date.
const hourOptions = [
  { id: '', text: '--:--' },
  ...Array.from({ length: 24 }, (_, h) => ({ id: h, text: `${h < 10 ? '0' : ''}${h}:00` })),
];

// Legacy `ddlOrderType_SelectedIndexChanged` defaulted the Time Requested
// dropdown to 08:00 for Gas orders and 00:00 (midnight) for every other order
// type (Liquid / Dewar).
const defaultRequestedHour = (orderTypeId: number) => (orderTypeId === OrderType.Gas ? 8 : 0);

// The Create Order submit button — placed in each tab's footer so it is always
// available, mirroring the edit page's per-tab action bar.
const createFooter = [
  { type: FormItemType.submitButton, label: 'Create Order', permissions: ordersPermissions },
];

/**
 * Create Order form — legacy `HeliumOrders.aspx` new-order parity, laid out as
 * horizontal tabs like the Order edit page. Field order, default values,
 * order-type conditional visibility and the customer-select auto-populate follow
 * the legacy behaviour. Order-only edit tabs (Manifest, Products Delivered,
 * Booking, RTU, Operations, Billing, Audit) are omitted — they require a saved
 * order — but the customer/location-scoped tabs are carried over.
 *
 * Every field `name` corresponds to a writable property on the backend
 * `OrderSaveRequest` DTO; server-computed values are read-only labels or omitted.
 */
export const ordersCreate = {
  pageHeader: 'Create Order',
  editMode: false,
  disableDirtyCheck: true,
  getCreateDefaults: (values: Record<string, unknown>) => {
    const orderTypeId = Number(values.orderTypeId);
    if (![OrderType.Gas, OrderType.Liquid, OrderType.Dewar].includes(orderTypeId) ||
      Object.prototype.hasOwnProperty.call(values, 'dateRequestedHour')) return values;
    return { ...values, dateRequestedHour: defaultRequestedHour(orderTypeId) };
  },
  permissions: ordersPermissions,
  breadcrumbs: [
    { name: 'Orders', icon: 'bag', to: '/orders' },
  ],
  // After a successful create, land on the new order's edit page (matching the
  // legacy redirect to the order Details view); fall back to the list.
  redirectOnCreate: (entity: any) =>
    entity?.id ? `/orders/edit/${entity.id}` : '/orders',
  items: [
    {
      type: FormItemType.tab,
      // Tab visibility depends on these live form fields (see tabHide* helpers).
      watchFields: ['orderTypeId', 'state'],
      items: [
        // ============================================================
        // Order Information
        // ============================================================
        {
          label: 'Order Information',
          footerItems: createFooter,
          items: [
            {
              type: FormItemType.fieldset,
              label: 'Order Information',
              className: 'compact-fields',
              items: [
                {
                  type: FormItemType.row,
                  className: 'legacy-order-columns',
                  balanceColumns: true,
                  items: [
                    {
                      type: FormItemType.row,
                      className: 'legacy-order-column',
                      items: [
                        {
                          name: 'orderTypeId',
                          label: 'Order Type',
                          type: FormItemType.select,
                          searchable: true,
                          required: true,
                          defaultValue: '',
                          onChange: ({ setValue, evt }: any) => {
                            setValue('customerId', '');
                            setValue('customerName', '');
                            setValue('deliveryTypeId', '');
                            setValue('carrierUsed', '');
                            const orderTypeId = Number(evt?.target?.value);
                            if (orderTypeId) {
                              setValue('dateRequestedHour', defaultRequestedHour(orderTypeId));
                            }
                          },
                          options: () => [
                            { id: '', text: '--Select One--' },
                            { id: OrderType.Gas, text: 'Gas' },
                            { id: OrderType.Liquid, text: 'Liquid' },
                            { id: OrderType.Dewar, text: 'Dewar' },
                          ],
                        },
                        {
                          type: FormItemType.row,
                          className: 'inline-pair',
                          items: [
                            { type: FormItemType.customComponent, component: OrderCustomerPickerButton, name: 'customerPicker', required: true },
                            { type: FormItemType.customComponent, component: OrderCustomerClipboard, name: 'customerClipboard', fitToContent: true },
                          ],
                        },
                        {
                          type: FormItemType.row,
                          className: 'inline-pair',
                          items: [
                            { name: 'dateRequested', label: 'Date Requested', type: FormItemType.date, required: true },
                            {
                              name: 'dateRequestedHour',
                              label: 'Time Requested',
                              type: FormItemType.select,
                              options: hourOptions,
                              // Coerce the native <select> string back to a number so the
                              // DateRequestedHour int? binds server-side.
                              onChange: ({ evt, setValue }: any) => {
                                const v = evt?.target?.value;
                                setValue('dateRequestedHour', v === '' || v == null ? null : Number(v));
                              },
                            },
                            { name: 'isForecastedOrder', label: 'Forecasted Order', type: FormItemType.checkboxField, defaultValue: false, hide: hideUnlessLiquid, fitToContent: true },
                          ],
                        },
                        { name: 'dateOrdered', label: 'Date Ordered', type: FormItemType.date, required: true, defaultValue: today },
                        { name: 'orderedBy', label: 'Ordered By Contact', type: FormItemType.customComponent, component: OrderContactField, maxLength: 100, defaultValue: '', required: true },
                        { name: 'orderedByPhone', label: 'Contact Phone #', maxLength: 40, defaultValue: '', required: true },
                        { name: 'orderedByEmail', label: 'Contact Email Address', maxLength: 100, defaultValue: '' },
                        {
                          name: 'orderMethod',
                          label: 'Order Method',
                          type: FormItemType.select,
                          searchable: true,
                          defaultValue: '',
                          options: () => [
                            { id: '', text: '-- Select One --' },
                            { id: 'Email', text: 'Email' },
                            { id: 'Phone', text: 'Phone' },
                            { id: 'Fax', text: 'Fax' },
                            { id: 'CAS', text: 'CAS' },
                            { id: 'Other', text: 'Other' },
                          ],
                        },
                        { name: 'ronumber', label: 'RO Number', maxLength: 60, defaultValue: '' },
                        {
                          name: 'carrierUsed',
                          label: 'Carrier',
                          type: FormItemType.select,
                          searchable: true,
                          defaultValue: null,
                          required: ({ watch }: any) => Number(watch?.('orderTypeId')) === OrderType.Gas,
                          zeroIsEmpty: true,
                          reloadOnFields: ['orderTypeId'],
                          options: async (_data: any, getValues: any) => {
                            const orderTypeId = Number(getValues?.('orderTypeId'));
                            if (!orderTypeId) return [{ id: '', text: '---Select One---' }];
                            const carriers = await getCarriers(orderTypeId);
                            return [
                              { id: '', text: '---Select One---' },
                              ...carriers.map((c: any) => ({ id: c.carrierId, text: c.carrierName })),
                            ];
                          },
                        },
                        { name: 'paperworkPending', label: 'Paperwork Pending', type: FormItemType.checkboxField, defaultValue: false, hide: hideUnlessLiquid },
                        {
                          type: FormItemType.row,
                          className: 'inline-pair',
                          items: [
                            { name: 'parentOrder', label: 'Parent Order', number: true, defaultValue: null },
                            { type: FormItemType.customComponent, component: OrderParentLink, name: 'parentOrderLink', fitToContent: true },
                          ],
                        },
                        { name: 'orderId', label: 'Order ID', maxLength: 60, defaultValue: '' },
                        { name: 'driverArrivalDateTime', label: 'Driver Arrival Date/Time', type: FormItemType.datetime, defaultValue: null },
                        { name: 'highPressureContainerReq', label: 'High Press. Cont. Request', type: FormItemType.checkboxField, defaultValue: false, hide: hideUnlessLiquid },
                      ],
                    },
                    {
                      type: FormItemType.row,
                      className: 'legacy-order-column',
                      items: [
                        {
                          name: 'deliveryTypeId',
                          label: 'Delivery Type',
                          type: FormItemType.select,
                          searchable: true,
                          required: true,
                          defaultValue: '',
                          // Delivery types are order-type-specific, so this stays
                          // disabled (with a hint) until an Order Type is chosen.
                          disabled: (watch: any) => !Number(watch?.('orderTypeId')),
                          disabledTooltip: 'Please select an Order Type first.',
                          reloadOnFields: ['orderTypeId'],
                          options: async (_data: any, getValues: any) => {
                            const orderTypeId = Number(getValues?.('orderTypeId'));
                            if (!orderTypeId) return [{ id: '', text: '---Select One---' }];
                            const dt = await getOrderDeliveryTypes(orderTypeId);
                            return [
                              { id: '', text: '---Select One---' },
                              ...dt.map((rec: any) => ({ id: rec.id, text: rec.name })),
                            ];
                          },
                        },
                        { name: 'address1', label: 'Shipping Address 1', maxLength: 200, defaultValue: '' },
                        { name: 'address2', label: 'Shipping Address 2', maxLength: 200, defaultValue: '' },
                        { name: 'city', label: 'City', maxLength: 100, defaultValue: '' },
                        { name: 'state', label: 'State', maxLength: 50, defaultValue: '' },
                        { name: 'zip', label: 'Zip', maxLength: 20, defaultValue: '' },
                        { name: 'country', label: 'Country', maxLength: 50, defaultValue: '' },
                        { name: 'orderedByFax', label: 'Contact Fax #', maxLength: 40, defaultValue: '' },
                        {
                          name: 'shippingLocationId',
                          label: 'Shipping Location',
                          type: FormItemType.select,
                          searchable: true,
                          required: true,
                          defaultValue: '',
                          options: async () => {
                            const locations = await getAllLocations();
                            return [
                              { id: '', text: '---Select One---' },
                              ...locations
                                .slice()
                                .sort((a: any, b: any) =>
                                  (a.locationName ?? '').trim().localeCompare((b.locationName ?? '').trim())
                                )
                                .map((l: any) => ({
                                  id: l.locationId,
                                  text: `${l.locationName}`,
                                })),
                            ];
                          },
                        },
                        { name: 'ponumber', label: 'PO Number', maxLength: 25, defaultValue: '' },
                        { name: 'paperworkReceivedDate', label: 'Paperwork Received Date', type: FormItemType.date, defaultValue: null, hide: hideUnlessLiquid },
                        { name: 'pronumber', label: 'PRO Number', maxLength: 25, defaultValue: '' },
                        {
                          name: 'driverUserId',
                          label: 'Driver',
                          type: FormItemType.select,
                          searchable: true,
                          defaultValue: '',
                          reloadOnFields: ['orderTypeId'],
                          options: async () => {
                            const users = await getActiveUsers();
                            return [
                              { id: '', text: '---Select One---' },
                              ...users
                                .filter((u: any) => u.isActive)
                                .map((u: any) => ({
                                  id: u.legacyId ?? u.activeDirectoryId ?? String(u.userId),
                                  text: u.displayName,
                                }))
                                .sort((a: any, b: any) => String(a.text).localeCompare(String(b.text))),
                            ];
                          },
                        },
                      ],
                    },
                    {
                      type: FormItemType.row,
                      className: 'legacy-order-column',
                      hide: hideUnlessDewar,
                      items: [
                        { name: 'dewarParentAccount', label: 'Parent Account', type: FormItemType.labelField },
                        { name: 'dewarAccountType', label: 'Account Type', type: FormItemType.labelField },
                        { name: 'dewarPoInfo', label: 'PO Information', type: FormItemType.labelField },
                        { name: 'dewarBillingStatus', label: 'Billing Status', type: FormItemType.labelField },
                        { name: 'dewarCreditManager', label: 'Credit Manager', type: FormItemType.labelField },
                        { name: 'dewarTerms', label: 'Terms', type: FormItemType.labelField },
                        { name: 'dateFulfilled', label: 'Date/Time Fulfilled', type: FormItemType.datetime, defaultValue: null },
                        { name: 'creditCardPayment', label: 'Credit Card Payment', type: FormItemType.checkboxField, defaultValue: false },
                        { type: FormItemType.customComponent, component: OrderCreditCardStatus, name: 'orderCreditCardStatus' },
                        { type: FormItemType.customComponent, component: OrderCreditCardOverride, name: 'creditCardOverrideWidget' },
                        { name: 'dewarCbtNotes', label: 'CBT Notes', type: FormItemType.labelField },
                      ],
                    },
                  ],
                },
                {
                  type: FormItemType.row,
                  className: 'legacy-order-notes',
                  items: [{ name: 'deliveryInstructions', label: 'Delivery Instructions', type: FormItemType.textarea, defaultValue: '' }],
                },
                {
                  type: FormItemType.row,
                  className: 'legacy-order-notes',
                  items: [{ name: 'otherNotes', label: 'Order Notes', type: FormItemType.textarea, defaultValue: '' }],
                },
                {
                  type: FormItemType.row,
                  className: 'legacy-order-notes',
                  items: [{ name: 'logisticsComments', label: 'Logistics Only Notes', type: FormItemType.textarea, defaultValue: '' }],
                },
                {
                  type: FormItemType.row,
                  className: 'legacy-order-notes',
                  items: [{ name: 'orderWarnings', label: 'Warnings', type: FormItemType.labelField }],
                },
              ],
            },
          ],
        },
        // ============================================================
        // International Dewar Info (legacy divIntlDewars) — Dewar international
        // orders only (ship-to state ZZ / PR / GU).
        // ============================================================
        {
          label: 'International Dewar Info',
          hide: tabHideUnlessIntlDewar,
          footerItems: createFooter,
          items: [
            {
              type: FormItemType.fieldset,
              label: 'International Dewar Information',
              className: 'compact-fields',
              items: [
                {
                  type: FormItemType.row,
                  items: [
                    { name: 'itn', label: 'ITN #', maxLength: 60, defaultValue: '' },
                    { name: 'dewarList', label: 'Dewar Serial Numbers', maxLength: 500, defaultValue: '' },
                    {
                      name: 'transportMode',
                      label: 'Transportation Mode',
                      type: FormItemType.select,
                      searchable: true,
                      defaultValue: '',
                      options: () => [
                        { id: '', text: '-- Select One --' },
                        { id: 'Air', text: 'Air' },
                        { id: 'Land', text: 'Land' },
                        { id: 'Sea', text: 'Sea' },
                      ],
                    },
                  ],
                },
                {
                  type: FormItemType.row,
                  items: [
                    // Legacy tbIntlDewarAirTransportCharge → AirFreight.
                    { name: 'airFreight', label: 'Air Transportation Charge', maxLength: 30, defaultValue: '' },
                    // Legacy tbIntlDewarLandTransportCharge → InlandFreight.
                    { name: 'inlandFreight', label: 'Land Transportation Charge', maxLength: 30, defaultValue: '' },
                    // Legacy tbIntlDewarSeaTransportCharge → OceanFreight.
                    { name: 'oceanFreight', label: 'Sea Transportation Charge', maxLength: 30, defaultValue: '' },
                  ],
                },
                {
                  type: FormItemType.row,
                  items: [{ name: 'exWorksOverride', label: 'ExWorks Override', type: FormItemType.checkboxField, defaultValue: false }],
                },
              ],
            },
          ],
        },
        // ============================================================
        // Container Information (legacy divContainerInformation + ddlBooking) —
        // Gas/Liquid. Container / returning-container / booking selects + the
        // container info panel. Legacy InsertNewOrder saves ContainerID /
        // ReturningContainerID / BookingID at create time.
        // ============================================================
        {
          label: 'Container Information',
          hide: tabHideUnlessGasOrLiquid,
          footerItems: createFooter,
          items: [
            {
              type: FormItemType.fieldset,
              label: 'Container & Booking',
              className: 'compact-fields',
              items: [
                {
                  type: FormItemType.row,
                  items: [
                    {
                      name: 'containerId',
                      label: 'Container Select',
                      type: FormItemType.select,
                      searchable: true,
                      // 'containerShowAll' is the legacy "All Containers" checkbox
                      // (cbUnfiltered): load every active container vs. only those
                      // filtered by shipping location.
                      reloadOnFields: ['orderTypeId', 'shippingLocationId', 'containerShowAll'],
                      options: async (_data: any, getValues: any) => {
                        const orderTypeId = Number(getValues?.('orderTypeId'));
                        const shippingLocationId = getValues?.('shippingLocationId');
                        const showAll = !!getValues?.('containerShowAll');
                        if (!orderTypeId) return [{ id: '', text: '-- Select One --' }];
                        const list = await getContainerOptions(orderTypeId, shippingLocationId ?? null, showAll, null);
                        return [
                          { id: '', text: '-- Select One --' },
                          ...(list ?? []).map((c: any) => ({ id: c, text: c })),
                        ];
                      },
                    },
                    { name: 'containerShowAll', label: 'All Containers', type: FormItemType.checkboxField, defaultValue: false },
                  ],
                },
                {
                  type: FormItemType.row,
                  items: [
                    {
                      name: 'returningContainerId',
                      label: 'Returning Container',
                      type: FormItemType.select,
                      searchable: true,
                      // 'returningContainerShowAll' is the legacy "All Containers"
                      // checkbox (cbUnfilteredReturning).
                      reloadOnFields: ['orderTypeId', 'customerId', 'returningContainerShowAll'],
                      options: async (_data: any, getValues: any) => {
                        const orderTypeId = Number(getValues?.('orderTypeId'));
                        const customerId = getValues?.('customerId');
                        const showAll = !!getValues?.('returningContainerShowAll');
                        const head = { id: '', text: '-- Select One --' };
                        if (!orderTypeId || (!customerId && !showAll)) return [head];
                        const list = await getReturningContainerOptions(orderTypeId, customerId ?? 0, showAll);
                        return [head, ...(list ?? []).map((c: any) => ({ id: c, text: c }))];
                      },
                    },
                    { name: 'returningContainerShowAll', label: 'All Containers', type: FormItemType.checkboxField, defaultValue: false },
                  ],
                },
                {
                  type: FormItemType.row,
                  items: [
                    {
                      name: 'bookingId',
                      label: 'Booking Select',
                      type: FormItemType.select,
                      searchable: true,
                      reloadOnOpen: true,
                      reloadOnFields: ['customerId'],
                      options: async (_data: any, getValues: any) => {
                        const customerId = getValues?.('customerId');
                        if (!customerId) return [{ id: '', text: '--Select One--' }];
                        const list = await getAvailableBookings(customerId, null);
                        return [
                          { id: '', text: '--Select One--' },
                          ...(list ?? []).map((b: any) => ({ id: b.bookingId, text: `${b.bookingId} - ${b.customerName ?? ''}`.trim() })),
                        ];
                      },
                    },
                  ],
                },
                // Container Information panel (legacy SetContainerInfo) — customer
                // requirements + selected container specs/readings. Reads the live
                // form via `watch` (refreshes as type / customer / container change).
                {
                  type: FormItemType.asyncDataPanel,
                  name: 'orderContainerInfo',
                  request: getContainerInfo,
                  requestArgs: (watch: any) => [Number(watch?.('orderTypeId')), watch?.('customerId'), watch?.('containerId') ?? null, 0],
                  watchFields: ['orderTypeId', 'customerId', 'containerId'],
                  sections: [
                    {
                      title: 'Customer Container Requirements',
                      rows: [
                        { headerKey: 'customerHeader1', valueKey: 'customerValue1' },
                        { headerKey: 'customerHeader2', valueKey: 'customerValue2' },
                      ],
                    },
                    {
                      title: 'Container Details',
                      rows: [
                        { headerKey: 'containerHeader1', valueKey: 'containerValue1' },
                        { headerKey: 'containerHeader2', valueKey: 'containerValue2', dangerKey: 'mawpMismatch' },
                        { headerKey: 'containerHeader3', valueKey: 'containerValue3' },
                        { headerKey: 'containerHeader4', valueKey: 'containerValue4', dangerKey: 'statusWarning' },
                      ],
                    },
                  ],
                  chart: {
                    dataKey: 'readings',
                    xKey: 'readingTime',
                    series: [
                      { name: 'He Level', dataKey: 'heLevel' },
                      { name: 'He Pressure', dataKey: 'hePressure' },
                      { name: 'N2 Level', dataKey: 'n2Level' },
                      { name: 'N2 Pressure', dataKey: 'n2Pressure' },
                    ],
                  },
                  footerKeys: ['latestReadings', 'lastLocation'],
                  link: {
                    text: 'View Map',
                    showKey: 'readings',
                    url: (watch: any) => `https://vts.linde.grp/Helium/VTS/Liquid/LiquidTrackingMap.aspx?container=${watch?.('containerId') ?? ''}`,
                  },
                },
              ],
            },
          ],
        },
        // ============================================================
        // Current Customer Information (legacy divCurrentCustomerInfo) — the
        // customer-scoped grids, gated per order type. Grids read the live
        // customer via the orderCreateContext data source.
        // ============================================================
        {
          label: 'Current Customer',
          hide: tabHideUntilOrderType,
          footerItems: createFooter,
          items: [
            {
              type: FormItemType.collapsePanel,
              label: 'Recent Deliveries',
              defaultOpen: true,
              hide: hideUnlessGas,
              items: [{ type: FormItemType.customComponent, component: GasRecentDeliveriesListView, name: 'orderGasRecentDeliveries' }],
            },
            {
              type: FormItemType.collapsePanel,
              label: 'Recent Deliveries',
              defaultOpen: true,
              hide: hideUnlessLiquid,
              items: [{ type: FormItemType.customComponent, component: LiquidRecentDeliveriesListView, name: 'orderLiquidRecentDeliveries' }],
            },
            {
              type: FormItemType.collapsePanel,
              label: 'Containers On Site',
              defaultOpen: true,
              hide: hideUnlessGas,
              items: [{ type: FormItemType.customComponent, component: GasContainersOnSiteListView, name: 'orderGasContainersOnSite' }],
            },
            {
              type: FormItemType.collapsePanel,
              label: 'Containers On Site',
              defaultOpen: true,
              hide: hideUnlessLiquid,
              items: [{ type: FormItemType.customComponent, component: LiquidContainersOnSiteListView, name: 'orderLiquidContainersOnSite' }],
            },
            {
              type: FormItemType.collapsePanel,
              label: 'Customer Purity Requirements',
              defaultOpen: true,
              hide: hideIfDewar,
              items: [{ type: FormItemType.customComponent, component: CustomerPurityListView, name: 'orderCustomerPurity' }],
            },
            {
              type: FormItemType.collapsePanel,
              label: 'Recent Dewar Deliveries',
              defaultOpen: true,
              hide: hideUnlessDewar,
              items: [{ type: FormItemType.customComponent, component: RecentDewarDeliveriesListView, name: 'orderRecentDewarDeliveries' }],
            },
            {
              type: FormItemType.collapsePanel,
              label: 'Dewar Containers On Site',
              defaultOpen: true,
              hide: hideUnlessDewar,
              items: [{ type: FormItemType.customComponent, component: DewarContainersOnSiteListView, name: 'orderDewarContainersOnSite' }],
            },
            {
              type: FormItemType.collapsePanel,
              label: 'Upcoming Orders',
              defaultOpen: true,
              hide: hideUnlessDewar,
              items: [{ type: FormItemType.customComponent, component: UpcomingOrdersForCustomerListView, name: 'orderUpcomingForCustomer' }],
            },
          ],
        },
        // ============================================================
        // Shipping Location Status (legacy divShippingLocationStatus) — upcoming
        // placed orders for the selected shipping location.
        // ============================================================
        {
          label: 'Shipping Location Status',
          hide: tabHideUntilOrderType,
          footerItems: createFooter,
          items: [
            {
              type: FormItemType.collapsePanel,
              label: 'Upcoming Orders',
              defaultOpen: true,
              items: [{ type: FormItemType.customComponent, component: OrderLocationStatusListView, name: 'orderLocationStatus' }],
            },
          ],
        },
      ],
    },
  ],
} as FormView;

export const OrdersCreateFormView = initView({ form: ordersCreate as any, dataSource: ordersCreateDS as any });
